"use client";

import { useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import {
  Button,
  Checkbox,
  Chip,
  Radio,
  Typography,
} from "@cappelendamm/design-system/react";
import { getImageBySize } from "@cappelendamm/catalogue/crystallize/utils/images.js";
import { GiftOption } from "@/checkout/Sections/SubscriptionSection/types";
import { GiftOptionTile } from "./GiftOptionTile";
import { getGiftNoun } from "./giftSelectionStatus";
import type { GiftSelectionFormValues } from "./GiftSelectorForm";

const MAX_VISIBLE_OPTIONS = 8;

type GiftSectionPickerProps = {
  sectionIndex: number;
  count: number;
  options: GiftOption[];
};

const getFixedTitle = (options: GiftOption[], count: number): string => {
  const allBooks = options.every((option) => option.isBook);
  if (allBooks) return count > 1 ? "Dine bøker" : "Din bok";
  return count > 1 ? "Dine gaver" : "Din gave";
};

export const GiftSectionPicker = ({
  sectionIndex,
  count,
  options,
}: GiftSectionPickerProps) => {
  const [showAll, setShowAll] = useState(false);
  const {
    control,
    register,
    setValue,
    trigger,
    formState: { errors, isSubmitted },
  } = useFormContext<GiftSelectionFormValues>();
  const fieldName = `giftSections.${sectionIndex}.options` as const;

  useFieldArray({
    control,
    name: fieldName,
    rules: {
      validate: (value) =>
        value.filter((option) => option.selected).length === count ||
        `Du må velge ${count} ${getGiftNoun(options, count)}`,
    },
  });

  const watchedOptions = useWatch({ control, name: fieldName }) ?? options;
  const selectedCount = watchedOptions.filter(
    (option) => option?.selected,
  ).length;

  const isChoice = options.length > count;
  const isRadio = isChoice && count === 1;
  const isFull = selectedCount >= count;
  const title = isChoice
    ? `Velg ${count} ${getGiftNoun(options, count)}`
    : getFixedTitle(options, count);
  const errorMessage = errors.giftSections?.[sectionIndex]?.options?.root
    ?.message as string | undefined;

  const visibleOptions =
    isChoice && !showAll && options.length > MAX_VISIBLE_OPTIONS
      ? options.slice(0, MAX_VISIBLE_OPTIONS)
      : options;
  const hiddenCount = options.length - visibleOptions.length;

  const selectOnly = (selectedIndex: number) => {
    options.forEach((_, index) => {
      setValue(`${fieldName}.${index}.selected`, index === selectedIndex, {
        shouldDirty: true,
      });
    });
    if (isSubmitted) void trigger(fieldName);
  };

  return (
    <section
      aria-labelledby={`gift-section-${sectionIndex}`}
      className="flex flex-col gap-4"
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-3">
          <Typography
            as="h2"
            variant="title-lg"
            id={`gift-section-${sectionIndex}`}
          >
            {title}
          </Typography>
          {isChoice ? (
            <Chip
              variant="secondary-small"
              className="shrink-0 text-sm font-medium"
            >
              {selectedCount} av {count} valgt
            </Chip>
          ) : null}
        </div>
        {errorMessage ? (
          <p role="alert" className="text-status-error-500">
            {errorMessage}
          </p>
        ) : null}
      </div>

      <ul className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-4 sm:gap-x-5">
        {visibleOptions.map((option, index) => {
          const isSelected = Boolean(watchedOptions[index]?.selected);
          const isDisabled = isChoice && !isRadio && !isSelected && isFull;
          const inputId = `gift-${sectionIndex}-${option.sku ?? index}`;
          // Gift images stop at 500 px, and their original can be a TIFF.
          const image = getImageBySize(option.images, 500);

          const control = !isChoice ? undefined : isRadio ? (
            <Radio
              id={inputId}
              name={`gift-section-${sectionIndex}`}
              value={index}
              checked={isSelected}
              onChange={() => selectOnly(index)}
            />
          ) : (
            <Checkbox
              id={inputId}
              checked={isSelected}
              disabled={isDisabled}
              {...register(`${fieldName}.${index}.selected`, {
                onChange: () => {
                  if (isSubmitted) void trigger(fieldName);
                },
              })}
            />
          );

          return (
            <li key={option.sku ?? index}>
              <GiftOptionTile
                name={option.name}
                author={option.author}
                imageSrc={image.src ?? undefined}
                control={control}
                inputId={inputId}
                isSelected={isSelected}
                isDisabled={isDisabled}
              />
            </li>
          );
        })}
      </ul>

      {hiddenCount > 0 ? (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setShowAll(true)}
          >
            Vis alle {options.length} {getGiftNoun(options, options.length)}
          </Button>
        </div>
      ) : null}
    </section>
  );
};
